import { pxArrayToRem, pxToRem } from '@archipro-design/aria';
import { PADDING_LEFT_RIGHT_SECTION_MOBILE } from '../../common/pagebuilder.style';
import type { StyleRule } from '@archipro-design/aria';
import type { ColumnDimensionType } from '@modules/page-builder/types';

export const ImageContainer: StyleRule<{
    dimension?: ColumnDimensionType;
    showActionsOnHover?: boolean;
}> = ({ theme, dimension, showActionsOnHover }) => ({
    position: 'relative',
    height: dimension?.mobileHeight ? pxToRem(dimension.mobileHeight) : '100%',
    width: dimension?.mobileWidth ? pxToRem(dimension.mobileWidth) : '100%',
    overflow: 'hidden',
    margin: 'auto',

    [theme.screen.tablet]: {
        height: dimension?.tabletHeight
            ? pxToRem(dimension.tabletHeight)
            : '100%',
        width: dimension?.tabletWidth ? pxToRem(dimension.tabletWidth) : '100%',
        marginTop: pxToRem(0),
    },

    [theme.screen.laptop]: {
        width: '100%',
        height: '100%',
        aspectRatio:
            dimension?.width && dimension?.height
                ? `${dimension.width} / ${dimension.height}`
                : undefined,
        marginTop: pxToRem(0),
        '& [role="toolbar"]': {
            opacity: showActionsOnHover ? 0 : 1,
        },
        '&:hover [role="toolbar"]': {
            opacity: 1,
        },
    },
});

export const HoverActions: StyleRule = ({ theme }) => ({
    position: 'absolute',
    margin: pxToRem(14),
    top: 0,
    right: 0,
    display: 'flex',
    transition: 'opacity 300ms',
    zIndex: theme.siteVariables.zIndexes.menuItem,
});

export const BackButton: StyleRule = () => ({
    position: 'absolute',
    top: pxToRem(15),
});

export const BannerWrapper: StyleRule<{
    showOverlay: boolean;
    dimension?: ColumnDimensionType;
}> = ({ theme, showOverlay, dimension }) => ({
    position: 'relative',
    [theme.screen.laptop]: {
        width: '100%',
        overflow: 'hidden',
        // Use desktop dimensions for proper aspect ratio on larger screens
        ...(dimension?.width &&
            dimension?.height && {
                aspectRatio: `${dimension.width} / ${dimension.height}`,
            }),
    },

    ...(showOverlay && {
        [`&:after`]: {
            content: `''`,
            background: `rgba(0,0,0,0)`,
            display: 'block',
            width: `100%`,
            height: `100%`,
            zIndex: theme.siteVariables.zIndexes.foreground,

            [theme.screen.laptop]: {
                position: 'absolute',
                top: 0,
                left: 0,
            },
        },
    }),
});

export const MobileCaptionContainer: StyleRule<
    | {
          dimension: ColumnDimensionType;
          showActionsOnHover: boolean;
          showOverlay: boolean;
      }
    | {
          dimension?: ColumnDimensionType;
          moduleType?: string;
          isProject?: boolean;
      }
> = ({ theme, ...props }) => {
    const moduleType = 'moduleType' in props ? props.moduleType : undefined;
    const isProject = 'isProject' in props ? props.isProject : undefined;

    return {
        padding: pxArrayToRem([0, PADDING_LEFT_RIGHT_SECTION_MOBILE]),
        [theme.screen.tablet]: {
            padding: 0,
        },

        [`& > div`]: {
            margin: 'auto',
            ...((moduleType === 'SingleImageModule' ||
                moduleType === 'FullPageGalleryModule') &&
                isProject && {
                    margin: 0,
                }),
        },
    };
};
